import { useCallback, useEffect, useMemo } from 'react'
import { useStepNavigationStore } from '@/stores/stepNavigationStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { useMapStore } from '@/stores/mapStore'
import { useRoute } from './useRoute'
import { useZoomToPoint } from './useZoomToPoint'
import { getMapCoordinates } from '@/components/shared/map/point/mapCoordinates'

export const useStepRouteNavigation = () => {
  const stage = useMapStore((state) => state.stage)
  const size = useMapStore((state) => state.size)
  const isRouteActive = useNavigationStore((state) => state.isRouteActive)
  const routeFromId = useNavigationStore((state) => state.route.from?.id)
  const routeToId = useNavigationStore((state) => state.route.to?.id)
  const { currentStep, reset, setStep } = useStepNavigationStore()
  const route = useRoute()
  const { zoomToPoint } = useZoomToPoint()

  const zoomToStep = useCallback(
    (stepIndex: number, animate = true) => {
      const point = route[stepIndex]
      if (!point || !stage) return

      const [width, height] = size
      const [x, y] = point

      zoomToPoint(
        stage,
        getMapCoordinates(width, x),
        getMapCoordinates(height, y),
        0.7,
        animate,
      )
    },
    [route, stage, size, zoomToPoint],
  )

  const zoomToStart = useCallback(
    (animate = true) => zoomToStep(0, animate),
    [zoomToStep],
  )

  useEffect(() => {
    if (!isRouteActive || !routeFromId || !routeToId || route.length === 0) return

    const timer = window.setTimeout(() => zoomToStart(true), 150)
    return () => window.clearTimeout(timer)
  }, [isRouteActive, routeFromId, routeToId, route.length, zoomToStart])

  const activeSegment = useMemo(() => {
    if (route.length < 2 || currentStep >= route.length - 1) return []
    return [route[currentStep], route[currentStep + 1]]
  }, [route, currentStep])

  const goToNext = () => {
    const nextIndex = currentStep + 1
    if (nextIndex >= route.length) return

    setStep(nextIndex)
    zoomToStep(nextIndex, true)
  }

  const isFinished = currentStep >= route.length - 1
  const totalSteps = route.length

  return {
    route,
    activeSegment,
    currentStep,
    totalSteps,
    goToNext,
    reset,
    zoomToStart,
    isFinished,
  }
}
